<template lang="pug">
  v-slide-y-transition(mode="out-in")

    v-container(fluid v-if="!isLoading()")

      confirm(ref="deleteConfirm")

      v-form(ref="roomForm", @submit='handleSubmit')

        v-card(class="mb2")

          v-card-title
            h3.headline Datos generales

          v-card-text
            v-text-field(:label="$t('Nombre')", v-model="room.name", :rules="validation.required", required)

            v-row(v-if="accommodation.accommodationType === 'HOTEL'")
              v-col(xs12 sm8)
                v-select(:label="$t('Tipo de habitación')", v-model="room.roomType", return-object, :rules="validation.required", required, :items="roomTypes")
              v-col(xs12 sm4)
                v-text-field(:label="$t('Habitaciones disponibles')", v-model="room.availableRooms", :rules="validation.numeric", required)

            v-row(v-else)
              v-col(xs12 sm6)
                v-text-field(:label="$t('Minimo personas')", v-model="room.minPeople", :rules="validation.numeric", required)
              v-col(xs12 sm6)
                v-text-field(:label="$t('Máximo personas')", v-model="room.maxPeople", :rules="validation.numeric", required)

            v-row
              v-col(xs6 sm2)
                v-checkbox(label="Oferta", @change="handleSaleChange()", v-model="room.sale")
              v-col(xs6 sm3)
                v-checkbox(label="Paquete / experiencia", @change="handleExperienceChange()", v-model="room.experience")
              v-col(xs6 sm2)
                v-checkbox(label="Visible", v-model="room.visible")
              v-col(xs6 sm2)
                v-checkbox(label="Bajo petición", v-model="defaultRate.onDemand")

            v-textarea(:label="$t('Notas')", v-model="room.notes", required, hint="Políticas de cancelación / servicios del alojamiento...")

          v-card-title
            h3.headline Tarifas

          v-card-text
            v-btn(@click="updateYear()") Actualizar tarifas y cierres al año actual

            v-card(class="mb-3 mt-3", v-if="!room.sale")
              v-card-title
                h3.headline Tarifa base

              v-card-text
                v-row
                  v-col(xs12 sm4)
                    v-text-field.input-text-right(label="Precio de contrato", v-model="defaultRate.contractPrice", @keyup="calculatePrice(defaultRate)", append-icon="euro_symbol", :rules="validation.numeric", required)
                  v-col(xs12 sm4)
                    v-text-field.input-text-right(label="Precio de venta", v-model="defaultRate.price", append-icon="euro_symbol", :rules="validation.numeric", required)
                  v-col(xs12 sm4)
                    v-text-field(label="Estancia mínima", v-model="defaultRate.minimumStay", :rules="validation.numeric", required)

            v-card(class="mb-3", v-for="(rate, $index) in room.rates", v-if="!rate.isDefault", :key="$index", :color="hasOverlaps ? 'red' : ''", :dark="hasOverlaps")
              v-card-title
                h3.headline Tarifa del {{rate.fromDate ? formatDate(rate.fromDate) : '-'}} a {{ rate.toDate ? formatDate(rate.toDate) : '-'}}
                v-spacer
                v-btn(color="red", dark fab small, @click="deleteRate(rate)")
                  v-icon delete
              v-card-text
                v-row
                  v-col(xs12 sm6)
                    v-menu(:close-on-content-click="false", transition="scale-transition", offset-y, :nudge-right="40", min-width="290px", max-width="290px")
                      template(v-slot:activator="{ on, attrs }")
                        v-text-field(v-on="on", v-bind="attrs", :value="formatDate(rate.fromDate)", label="Desde", prepend-icon="event", readonly, :rules="validation.required", required)
                      v-date-picker(locale="es", :first-day-of-week="1", no-title, autosave, v-model="rate.fromDate", @input="checkOverlaps()")
                  v-col(xs12 sm6)
                    v-menu(:close-on-content-click="false", transition="scale-transition", offset-y, :nudge-right="40", min-width="290px", max-width="290px")
                      template(v-slot:activator="{ on, attrs }")
                        v-text-field(v-on="on", v-bind="attrs", :value="formatDate(rate.toDate)", label="Hasta", prepend-icon="event", readonly, :rules="validation.required", required)
                      v-date-picker(locale="es", :first-day-of-week="1", no-title, autosave, v-model="rate.toDate", @input="checkOverlaps()")
                  v-col(xs12 sm4)
                    v-text-field.input-text-right(label="Precio de contrato", v-model="rate.contractPrice", @keyup="calculatePrice(rate)", append-icon="euro_symbol", :rules="validation.numeric", required)
                  v-col(xs12 sm4)
                    v-text-field.input-text-right(label="Precio de venta", v-model="rate.price", append-icon="euro_symbol", :rules="validation.numeric", required)
                  v-col(xs12 sm4)
                    v-text-field(label="Estancia mínima", v-model="rate.minimumStay", :rules="validation.numeric", required)
                  v-checkbox(label="Semana completa", v-model="rate.fullWeek", @change="checkOverlaps()")
                  v-checkbox(label="Bajo petición", v-model="rate.onDemand")
                v-layout(align-center, v-if="!rate.fullWeek", style="border-top: 1px solid #DDD; padding-top: 30px")
                  v-checkbox(v-model="rate.weekDays", value="MON", label="Lunes")
                  v-checkbox(v-model="rate.weekDays", value="TUE", label="Martes")
                  v-checkbox(v-model="rate.weekDays", value="WED", label="Miércoles")
                  v-checkbox(v-model="rate.weekDays", value="THU", label="Jueves")
                  v-checkbox(v-model="rate.weekDays", value="FRI", label="Viernes")
                  v-checkbox(v-model="rate.weekDays", value="SAT", label="Sábado")
                  v-checkbox(v-model="rate.weekDays", value="SUN", label="Domingo")

            v-btn(color="success", class="ml-0", @click="addRate()")
              v-icon add
              span Añadir nueva tarifa

          v-card-title
            h3.headline Cierres

          v-card-text
            v-alert(color="orange", class="mt-0", icon="info", :value="room.closes.length < 1", outlined) Todavía no hay ningún cierre

            v-card(class="mb-3", v-for="(close, $index) in room.closes", :key="$index")
              v-card-text
                v-layout(row)
                  v-col(xs12 sm6)
                    v-menu(:close-on-content-click="false", transition="scale-transition", offset-y, :nudge-right="40", min-width="290px", max-width="290px")
                      template(v-slot:activator="{ on, attrs }")
                        v-text-field(v-on="on", v-bind="attrs", :value="formatDate(close.fromDate)", label="Desde", prepend-icon="event", readonly, :rules="validation.required", required)
                      v-date-picker(locale="es", :first-day-of-week="1", no-title, autosave, v-model="close.fromDate")
                  v-col(xs12 sm6)
                    v-menu(:close-on-content-click="false", transition="scale-transition", offset-y, :nudge-right="40", min-width="290px", max-width="290px")
                      template(v-slot:activator="{ on, attrs }")
                        v-text-field(v-on="on", v-bind="attrs", :value="formatDate(close.toDate)", label="Hasta", prepend-icon="event", readonly, :rules="validation.required", required)
                      v-date-picker(locale="es", :first-day-of-week="1", no-title, autosave, v-model="close.toDate")
                  v-col
                    v-btn(color="red", dark fab small, @click="deleteClose(close)")
                      v-icon delete

            v-btn(color="orange", class="ml-0", dark, @click="addClose()")
              v-icon add
              span Añadir nuevo cierre

          v-card-actions
            v-btn(color="red" dark @click="$router.push(`/accommodations/${accommodationId}/rooms`)") Cancelar
            v-spacer
            v-btn(color="primary", type="submit") Guardar

    v-container(fluid fill-height v-else)
      v-layout(flex align-center justify-center)
        v-progress-circular(indeterminate, color="primary", :size="128", :width="2")
</template>

<style lang="postcss">
.input-text-right input {
  text-align: right;
}
</style>

<script>
import { round } from '@/utils/math';
import validation from '@/utils/validation';

const emptyRoom = () => {
  const room = {
    id: null,
    sale: false,
    experience: false,
    visible: true,
    name: null,
    availableRooms: null,
    roomType: null,
    minPeople: null,
    maxPeople: null,
    rates: [],
    closes: [],
  };
  return room;
};

const emptyRate = {
  isDefault: false,
  fromDate: null,
  toDate: null,
  contractPrice: null,
  price: null,
  minimumStay: null,
  onDemand: false,
  fullWeek: true,
  weekDays: [],
};

const emptyClose = {
  fromDate: null,
  toDate: null,
};

const emptyDefaultRate = Object.assign({}, emptyRate, {
  isDefault: true,
});

export default {
  data() {
    return {
      hasOverlaps: false,
      room: [],
      accommodationId: this.$route.params.accommodationId,
      roomTypes: [],
      loading: {
        room: false,
        accommodation: false,
        roomTypes: false,
        saving: false,
      },
      validation,
    };
  },
  computed: {
    defaultRate() {
      let defaultRate;
      const defaultRates = this.room.rates.filter(rate => rate.isDefault);
      if (defaultRates.length > 0) {
        defaultRate = defaultRates[0];
      } else {
        defaultRate = Object.assign({}, emptyDefaultRate);
        this.room.rates.push(defaultRate);
      }
      return defaultRate;
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    isLoading() {
      return (
        this.loading.room ||
        this.loading.accommodation ||
        this.loading.roomTypes ||
        this.loading.saving
      );
    },
    fetchData() {
      this.loading.room = true;
      this.loading.accommodation = true;
      this.loading.roomTypes = true;

      if (this.$route.params.id) {
        api
          .get(`/accommodations/${this.accommodationId}/rooms/${this.$route.params.id}`)
          .then((res) => {
            this.room = Object.assign({}, emptyRoom(), res.data);
            if (this.$route.path.includes('duplicate')) {
              this.room.id = null;
              this.room.rates = this.room.rates.map((r) => {
                const rate = r;
                rate.id = null;
                return r;
              });
              this.room.closes = this.room.closes.map((c) => {
                const close = c;
                close.id = null;
                return close;
              });
            }
            this.checkOverlaps();
          })
          .catch(this.handleError)
          .finally(() => {
            this.loading.room = false;
          });
      } else {
        this.room = Object.assign({}, emptyRoom());
        this.loading.room = false;
      }

      api
        .get(`/accommodations/${this.accommodationId}`)
        .then((res) => {
          this.accommodation = res.data;
        })
        .catch(this.handleError)
        .finally(() => {
          this.loading.accommodation = false;
        });

      api
        .get('/types/rooms')
        .then((res) => {
          this.roomTypes = res.data;
        })
        .catch(this.handleError)
        .finally(() => {
          this.loading.roomTypes = false;
        });
    },
    formatDate(date) {
      if (date) {
        const [year, month, day] = date.split('-');
        return `${day}/${month}/${year}`;
      }
      return date;
    },
    nextDay(date) {
      if (date) {
        const obj = new Date(date);
        obj.setDate(obj.getDate() + 1);
        return obj.toISOString().split('T')[0];
      }
      return date;
    },
    addRate() {
      this.room.rates.push(Object.assign({}, emptyRate));
    },
    updateYear() {
      const year = new Date().getFullYear();
      this.room.rates = this.room.rates.map((rate) => {
        const newRate = Object.assign({}, rate);
        if (newRate.fromDate) {
          const nFromDate = new Date(newRate.fromDate);
          nFromDate.setFullYear(year);
          newRate.fromDate = nFromDate.toISOString().split('T')[0];
        }
        if (newRate.toDate) {
          const nToDate = new Date(newRate.toDate);
          nToDate.setFullYear(year);
          newRate.toDate = nToDate.toISOString().split('T')[0];
        }
        return newRate;
      });
      this.room.closes = this.room.closes.map((close) => {
        const newClose = Object.assign({}, close);
        newClose.fromDate.setFullYear(year);
        newClose.toDate.setFullYear(year);
        return newClose;
      });
    },
    deleteRate(item) {
      this.$refs.deleteConfirm.open('Eliminar', '¿Estás seguro?').then((confirm) => {
        if (confirm) {
          if (item.id) {
            api
              .delete(
                `/accommodations/${this.accommodationId}/rooms/${this.room.id}/rates/${item.id}`,
              )
              .then(() => {
                this.room.rates.splice(this.room.rates.indexOf(item), 1);
              })
              .catch(this.handleError);
          } else {
            this.room.rates.splice(this.room.rates.indexOf(item), 1);
          }
        }
      });
    },
    addClose() {
      this.room.closes.push(Object.assign({}, emptyClose));
    },
    deleteClose(item) {
      this.$refs.deleteConfirm.open('Eliminar', '¿Estás seguro?').then((confirm) => {
        if (confirm) {
          if (item.id) {
            api
              .delete(`/accommodations/${this.accommodationId}/rooms/${this.room.id}/closes/${item.id}`)
              .then(() => {
                this.room.closes.splice(this.room.closes.indexOf(item), 1);
              })
              .catch(this.handleError);
          } else {
            this.room.closes.splice(this.room.closes.indexOf(item), 1);
          }
        }
      });
    },
    calculatePrice(rate) {
      let pvp = 0;
      switch (this.accommodation.priceType) {
        case 'NET_RAW':
          pvp = round(
            rate.contractPrice *
              (1 + (this.accommodation.agentCommission / 100)) *
              (1 + (this.accommodation.tax / 100)),
          );
          break;
        case 'NET_TAX':
          pvp = round(rate.contractPrice * (1 + (this.accommodation.agentCommission / 100)));
          break;
        case 'COMMISSIONABLE_RAW':
          pvp = round(
            rate.contractPrice *
              (1 + (this.accommodation.tax / 100)) *
              (1 - (this.accommodation.accommodationCommission / 100)) *
              (1 + (this.accommodation.agentCommission / 100)),
          );
          break;
        case 'COMMISSIONABLE_TAX':
          pvp = round(
            rate.contractPrice *
              (1 - (this.accommodation.accommodationCommission / 100)) *
              (1 + (this.accommodation.agentCommission / 100)),
          );
          break;
        default:
          this.handleError(`Unknown price type detected ${this.accommodation.priceType}`);
          break;
      }
      this.room.rates[this.room.rates.indexOf(rate)].price = pvp;
      this.$forceUpdate();
    },
    checkOverlaps() {
      let overlap = false;
      this.room.rates
        .filter(r => !r.isDefault && r.fullWeek)
        .forEach((rate) => {
          const fromDate = new Date(rate.fromDate);
          const toDate = new Date(rate.toDate);
          this.room.rates
            .filter(r => !r.isDefault && r.fullWeek)
            .filter(r => r !== rate)
            .forEach((inRate) => {
              const inRateFromDate = new Date(inRate.fromDate);
              const inRateToDate = new Date(inRate.toDate);
              if (
                (fromDate <= inRateToDate && fromDate >= inRateFromDate) ||
                (toDate <= inRateToDate && toDate >= inRateFromDate)
              ) {
                overlap = true;
              }
            });
        });
      this.hasOverlaps = overlap;
    },
    handleSaleChange() {
      this.room.experience = false;
    },
    handleExperienceChange() {
      this.room.sale = false;
    },
    handleSubmit() {
      if (this.$refs.roomForm.validate() && !this.hasOverlaps) {
        this.loading.saving = true;
        if (this.room.id === null) {
          api
            .post(`/accommodations/${this.accommodationId}/rooms`, this.room)
            .then(() => {
              this.$router.push(`/accommodations/${this.accommodationId}/rooms`);
            })
            .catch(this.handleError)
            .finally(() => {
              this.loading.saving = false;
            });
        } else {
          api
            .put(`/accommodations/${this.accommodationId}/rooms/${this.room.id}`, this.room)
            .then(() => {
              this.$router.push(`/accommodations/${this.accommodationId}/rooms`);
            })
            .catch(this.handleError)
            .finally(() => {
              this.loading.saving = false;
            });
        }
      }
    },
    handleError(err) {
      // eslint-disable-next-line no-console
      console.error(err);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/Accommodations/AccommodationRoomForm.vue